/**
 * Hand-written streaming client for POST /api/public/chat.
 * The endpoint streams Server-Sent Events, which Orval's generated
 * TanStack Query hooks cannot consume — this is the one sanctioned
 * exception to the generated-client rule (see openapi.yaml description).
 */

export type ChatStreamEvent =
  | { type: 'session'; sessionId: string }
  | { type: 'delta'; text: string }
  | { type: 'validation'; status: 'grounded' | 'low_confidence' | 'failed'; confidence: number }
  | { type: 'capture_requested'; requiredFields: string[]; optionalFields: string[]; questions: string[] }
  | { type: 'lead_captured'; contactId: string; enquiryId: string | null }
  | { type: 'enquiry_created'; enquiryId: string }
  | { type: 'error'; message: string }
  | { type: 'done' };

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Mirrors the OpenAPI PublicChatRequest caps. */
export const CHAT_MESSAGE_MAX = 2000;
export const CHAT_HISTORY_MAX = 20;

export async function streamChat(
  input: { message: string; history: ChatTurn[]; sessionId?: string; mode?: 'chat' | 'voice'; endpoint?: string },
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(input.endpoint ?? '/api/public/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      message: input.message,
      history: input.history.slice(-CHAT_HISTORY_MAX),
      sessionId: input.sessionId,
      mode: input.mode ?? 'chat'
    }),
    signal
  });

  if (!res.ok) {
    let message = 'The assistant is unavailable right now.';
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new Error(message);
  }

  if (!res.body) throw new Error('Streaming is not supported by this browser.');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let separator = buffer.indexOf('\n\n');
    while (separator !== -1) {
      const frame = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data: '))
        .map((line) => line.slice(6))
        .join('\n');
      if (data) {
        try {
          onEvent(JSON.parse(data) as ChatStreamEvent);
        } catch {
          // Skip malformed frames rather than killing the stream.
        }
      }
      separator = buffer.indexOf('\n\n');
    }
  }
}
